import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { metricById } from './metricsRegistry'

/**
 * The ONE way to render a metric (PRD-02 §5.6): label with a dotted-underline affordance,
 * a definition tooltip (300ms hover delay, focusable, Escape-dismissable, interactive so the
 * cursor can travel into it, tap-to-open on touch), and a "Full definition" link to the
 * glossary anchor. Values route to the metric's calculation page unless we're already on it.
 * The build fails if `id` isn't in the registry (scripts/check-metrics-registry.cjs).
 */
export const Metric: React.FC<{
  id: string
  value: React.ReactNode
  /** Optional label override (defaults to the registry displayName). */
  label?: string
  tone?: 'blue' | 'amber' | 'green'
  className?: string
  compact?: boolean
}> = ({ id, value, label, tone, className = '', compact = false }) => {
  const def = metricById(id)
  const [open, setOpen] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const show = () => { if (timer.current) clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(true), 300) }
  const hide = () => { if (timer.current) clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(false), 200) }

  const toneCls = tone === 'blue' ? 'text-brand-700' : tone === 'amber' ? 'text-amber-600' : tone === 'green' ? 'text-emerald-600' : 'text-txt-primary'
  const onCalcPage = def?.calcPageRoute && location.pathname === def.calcPageRoute
  const valueClickable = def?.calcPageRoute && !onCalcPage

  if (!def) {
    // Should be unreachable — the registry check fails the build first. Render honestly anyway.
    return (
      <div className={`bg-surface-tertiary/60 rounded-xl px-3 py-2 ${className}`}>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-red-600">unregistered metric: {id}</p>
        <p className="text-lg font-bold">{value}</p>
      </div>
    )
  }

  return (
    <div className={`relative bg-surface-tertiary/60 rounded-xl ${compact ? 'px-2.5 py-1.5' : 'px-3 py-2'} ${className}`} onMouseLeave={hide}>
      <button
        onMouseEnter={show}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen((v) => !v)}
        aria-describedby={open ? `metric-tip-${id}` : undefined}
        className="text-left"
      >
        <p className={`${compact ? 'text-[9px]' : 'text-[10px]'} font-semibold uppercase tracking-wider text-txt-tertiary border-b border-dotted border-txt-tertiary/60 inline-block cursor-help`}>
          {label || def.displayName}
          {def.incomplete && <span className="ml-1 text-amber-600" title="Definition incomplete — see glossary">⚑</span>}
        </p>
      </button>
      <p
        onClick={valueClickable ? () => navigate(def.calcPageRoute! + `?highlight=${id}`) : undefined}
        title={valueClickable ? `Calculated on ${def.calcPageRoute}` : undefined}
        className={`${compact ? 'text-base' : 'text-lg'} font-bold ${toneCls} ${valueClickable ? 'cursor-pointer hover:underline decoration-2 underline-offset-2' : ''}`}
      >
        {value}
      </p>

      {open && (
        <div
          id={`metric-tip-${id}`}
          role="tooltip"
          onMouseEnter={() => { if (timer.current) clearTimeout(timer.current) }}
          onMouseLeave={hide}
          className="absolute z-50 top-full left-0 mt-1 w-72 rounded-xl border border-border bg-surface shadow-lg p-3"
        >
          <p className="text-[12px] font-semibold text-txt-primary mb-1">{def.displayName}</p>
          <p className="text-[12px] text-txt-secondary leading-snug">
            {def.plainDefinition.length > 200 ? def.plainDefinition.slice(0, 197) + '…' : def.plainDefinition}
          </p>
          <p className="text-[10px] text-txt-tertiary mt-1.5">{def.unit} · {def.refreshFrequency}</p>
          {!def.calcPageRoute && <p className="text-[10px] text-txt-tertiary mt-1">Received pre-calculated — no single calculation page.</p>}
          <button
            onClick={() => { setOpen(false); navigate(`/crm/metrics#${def.id}`) }}
            className="mt-2 text-[11px] font-semibold text-brand-600 hover:underline"
          >
            Full definition →
          </button>
        </div>
      )}
    </div>
  )
}
